import { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router";
import { PageSpinner } from "@/components/PageSpinner";
import { ROUTES } from "@/router";
import { useAppStore } from "@/store/app/slice";
import { AuthState } from "@/types/common";
import { useAppInitialization } from "../hooks/useAppInitialization";
import { useAuthenticationRedirect } from "../hooks/useAuthenticationRedirect";
import type { IAuthContext, IAuthProviderProps } from "../types";

export const AuthContext = createContext<IAuthContext>({
  authenticated: AuthState.Loading,
} as IAuthContext);

export const useAuthContext = () => useContext(AuthContext);

export const AuthProvider = (props: IAuthProviderProps) => {
  const { pathname } = useLocation();
  const authenticated = useAppStore((s) => s.authenticated);
  const setAuthenticated = useAppStore((s) => s.setAuthenticated);

  // Initialize app authentication and user fetching
  useAppInitialization();
  // Handle authentication redirects for the entire app
  useAuthenticationRedirect();

  const context: IAuthContext = useMemo((): IAuthContext => {
    return {
      authenticated,
      setAuthenticated,
      ...props,
    };
  }, [authenticated, props, setAuthenticated]);

  if (pathname.startsWith(`/${ROUTES.LEGAL}`)) {
    return props.children;
  }

  return (
    <AuthContext.Provider value={context}>
      {authenticated === AuthState.Loading ? <PageSpinner /> : props.children}
    </AuthContext.Provider>
  );
};
